/**
 * Native WKWebView content-process termination diagnostics (observability only).
 *
 * The iOS shell (AppDelegate.swift, WebViewTerminationDiagnostics) writes a
 * small non-sensitive record to Capacitor Preferences each time WebKit
 * terminates the WebContent process, BEFORE Capacitor's unchanged
 * `bridge?.reset(); webView.reload()`. After the reload, this module forwards
 * those records to the existing telemetry pipeline as
 * `native.webview_content_process_terminated`, then clears them.
 *
 * This is NOT a crash event. Only allowlisted technical fields are sent;
 * anything else in the stored record is dropped. Never throws, never blocks.
 */
import { Capacitor } from "@capacitor/core";
import { captureEvent } from "@/lib/observability";

export const WEBVIEW_TERMINATION_EVENT = "native.webview_content_process_terminated";
/** Preferences key (native stores it as "CapacitorStorage.<key>"). */
export const WEBVIEW_TERMINATION_KEY = "tm_native_webview_terminations";

const STRING_FIELDS = [
  "source",
  "terminated_at",
  "app_version",
  "native_build",
  "ios_version",
  "device_model",
  "app_state",
  "route_category",
] as const;
const NUMBER_FIELDS = ["termination_count", "ms_since_previous_termination"] as const;
const ROUTE_CATEGORIES = new Set(["root", "dashboard", "appraisal", "resume_job", "job", "driver-management", "auth", "other"]);
const APP_STATES = new Set(["active", "inactive", "background", "unknown"]);

export function sanitiseTerminationRecord(raw: unknown): Record<string, string | number> | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.event !== WEBVIEW_TERMINATION_EVENT) return null;
  const out: Record<string, string | number> = { event: WEBVIEW_TERMINATION_EVENT };
  for (const k of STRING_FIELDS) {
    if (typeof r[k] === "string") out[k] = (r[k] as string).slice(0, 64);
  }
  for (const k of NUMBER_FIELDS) {
    if (typeof r[k] === "number" && Number.isFinite(r[k])) out[k] = Math.round(r[k] as number);
  }
  out.source = "wkwebview_content_process";
  if (!ROUTE_CATEGORIES.has(String(out.route_category))) out.route_category = "other";
  if (!APP_STATES.has(String(out.app_state))) out.app_state = "unknown";
  return out;
}

export function parseTerminationRecords(json: string | null | undefined): Record<string, string | number>[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-20).map(sanitiseTerminationRecord).filter((x): x is Record<string, string | number> => !!x);
  } catch {
    return [];
  }
}

export function emitTerminationRecord(meta: Record<string, string | number>): void {
  try {
    captureEvent({
      kind: "native",
      severity: "warn",
      component: "native.webview",
      message: WEBVIEW_TERMINATION_EVENT,
      route: String(meta.route_category),
      priority: "high",
      metadata: meta,
    });
  } catch {
    /* diagnostics only */
  }
}

/** Forward and clear any pending native termination records. iOS native only. */
export async function flushNativeWebViewTerminations(): Promise<number> {
  try {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return 0;
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: WEBVIEW_TERMINATION_KEY });
    if (!value) return 0;
    await Preferences.remove({ key: WEBVIEW_TERMINATION_KEY });
    const records = parseTerminationRecords(value);
    records.forEach(emitTerminationRecord);
    return records.length;
  } catch {
    return 0;
  }
}
